import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Star, Clock, Bike, Plus, ChevronRight, ChevronLeft, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function RestaurantDetail() {
  const { id } = useParams();
  const { t, lang, isRTL } = useLang();
  const { addItem, itemCount } = useCart();
  const [r, setR] = useState(null);
  const [bIdx, setBIdx] = useState(0);
  const navigate = useNavigate();
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  useEffect(() => {
    api.get(`/restaurants/${id}`).then(({ data }) => setR(data));
  }, [id]);

  const banners = useMemo(() => (Array.isArray(r?.banners) ? r.banners : []), [r]);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBIdx((i) => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);

  const categories = useMemo(() => {
    if (!r) return [];
    const set = new Set();
    r.menu.forEach((m) => set.add(m.category));
    return Array.from(set);
  }, [r]);

  // ---- search + active-category nav ----
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("");
  const sectionRefs = useRef({});
  const chipRefs = useRef({});
  const navigatingRef = useRef(false);
  const currentCat = activeCat || categories[0] || "";

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !r) return null;
    return r.menu.filter((m) => {
      const hay = `${m.name_ar || ""} ${m.name_en || ""} ${m.description_ar || ""} ${m.description_en || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, r]);

  const getScroller = useCallback((el) => {
    let node = el?.parentElement;
    while (node) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) return node;
      node = node.parentElement;
    }
    return null;
  }, []);

  const scrollToCat = useCallback((cat) => {
    const el = sectionRefs.current[cat];
    const scroller = getScroller(el);
    if (!el || !scroller) return;
    navigatingRef.current = true;
    setActiveCat(cat);
    const top =
      el.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      140; // clear sticky search + nav
    scroller.scrollTo({ top, behavior: "auto" });
    setTimeout(() => { navigatingRef.current = false; }, 300);
  }, [getScroller]);

  // scroll-spy: highlight the section currently in view
  useEffect(() => {
    if (!r || searchResults) return;
    const firstSec = Object.values(sectionRefs.current).find(Boolean);
    const scroller = getScroller(firstSec);
    const obs = new IntersectionObserver(
      (entries) => {
        if (navigatingRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveCat(visible[0].target.getAttribute("data-cat"));
      },
      { root: scroller || null, rootMargin: "-150px 0px -65% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [r, searchResults, categories, getScroller]);

  // keep the active chip scrolled into view in the horizontal bar
  useEffect(() => {
    const chip = chipRefs.current[currentCat];
    if (chip) chip.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [currentCat]);

  if (!r) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#E05D3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const name = lang === "ar" ? r.name_ar : r.name_en;
  const desc = lang === "ar" ? r.description_ar : r.description_en;

  const handleAdd = (item) => {
    addItem(r, item);
    toast.success(`${lang === "ar" ? item.name_ar : item.name_en} ${lang === "ar" ? "أضيف للسلة" : "added to cart"}`);
  };

  const renderItem = (item) => (
    <div key={item.id} className="jh-card p-4 flex gap-4" data-testid={`menu-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[#1A1A1A] line-clamp-2">{lang === "ar" ? item.name_ar : item.name_en}</h3>
        <p className="text-sm text-[#6B6B6B] mt-1 line-clamp-1">{lang === "ar" ? item.description_ar : item.description_en}</p>
        <p className="mt-3 font-bold text-[#E05D3A]">{item.price.toFixed(2)} SAR</p>
      </div>
      <div className="relative w-28 h-28 flex-shrink-0">
        <img src={item.image} alt="" loading="lazy" className="w-full h-full object-cover rounded-2xl bg-[#F0F0F0]" />
        <button
          onClick={() => handleAdd(item)}
          className="absolute -bottom-2 -end-2 w-10 h-10 rounded-full bg-[#E05D3A] text-white flex items-center justify-center shadow-lg hover:bg-[#C24B2C] transition-all hover:scale-110"
          data-testid={`add-item-${item.id}`}
          aria-label={t("addItem")}
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="pb-28">
      {/* Cover */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={r.cover_image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-6 pb-8">
          <Link to="/restaurants" className="text-white/80 text-sm hover:text-white inline-flex items-center gap-1 mb-3">
            <Chevron className="w-4 h-4 rotate-180" /> {t("restaurants")}
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">{name}</h1>
          <p className="text-white/85 mt-2 max-w-2xl">{desc}</p>
          <div className="mt-4 flex flex-wrap gap-5 text-white/90 text-sm">
            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[#E05D3A] text-[#E05D3A]" /> {r.rating.toFixed(1)}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {r.delivery_time} {t("min")}</span>
            <span className="inline-flex items-center gap-1"><Bike className="w-4 h-4" /> {r.delivery_fee} SAR</span>
            <span>{t("minOrder")}: {r.min_order} SAR</span>
          </div>
        </div>
      </div>

      {/* Promotional banners (animated slider) */}
      {banners.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-8" data-testid="store-banners">
          <div className="relative overflow-hidden rounded-3xl aspect-[1440/460] shadow-sm bg-[#F0F0F0]">
            {banners.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                loading={i === 0 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                style={{ opacity: i === bIdx ? 1 : 0 }}
                data-testid={`store-banner-${i}`}
              />
            ))}
            {banners.length > 1 && (
              <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setBIdx(i)}
                    aria-label={`banner ${i + 1}`}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === bIdx ? 20 : 6,
                      background: i === bIdx ? "#fff" : "rgba(255,255,255,0.6)",
                    }}
                    data-testid={`store-banner-dot-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky search + category nav */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#EEE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 start-4 w-5 h-5 text-[#9A9A9A]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "ar" ? `ابحث في ${name}…` : `Search ${name}…`}
              className="w-full h-12 rounded-full bg-[#F4F4F4] ps-12 pe-12 text-[#1A1A1A] outline-none focus:ring-2 focus:ring-[#E05D3A]/40 text-base"
              data-testid="store-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute top-1/2 -translate-y-1/2 end-3 w-8 h-8 rounded-full bg-[#E5E5E5] flex items-center justify-center hover:bg-[#D5D5D5]"
                data-testid="store-search-clear"
                aria-label={lang === "ar" ? "مسح" : "clear"}
              >
                <X className="w-4 h-4 text-[#555]" />
              </button>
            )}
          </div>
          {!searchResults && categories.length > 1 && (
            <div className="flex gap-2 overflow-x-auto hide-scrollbar py-3 -mx-1 px-1" data-testid="store-category-nav">
              {categories.map((cat) => (
                <button
                  key={cat}
                  ref={(el) => (chipRefs.current[cat] = el)}
                  onClick={() => scrollToCat(cat)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    currentCat === cat ? "bg-[#E05D3A] text-white" : "bg-[#F0F0F0] text-[#1A1A1A] hover:bg-[#E8E8E8]"
                  }`}
                  data-testid={`category-chip-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Menu / Search results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {searchResults ? (
          <div data-testid="store-search-results">
            <p className="text-sm text-[#6B6B6B] mb-5">
              {lang === "ar"
                ? `${searchResults.length} نتيجة لـ "${query}"`
                : `${searchResults.length} result(s) for "${query}"`}
            </p>
            {searchResults.length === 0 ? (
              <div className="text-center py-20 text-[#9A9A9A]" data-testid="store-search-empty">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-40" />
                <p className="text-lg font-medium text-[#6B6B6B]">
                  {lang === "ar" ? "لا توجد منتجات مطابقة" : "No matching products"}
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {searchResults.map(renderItem)}
              </div>
            )}
          </div>
        ) : (
          <div>
            {categories.map((cat) => (
              <section
                key={cat}
                ref={(el) => (sectionRefs.current[cat] = el)}
                data-cat={cat}
                className="mb-12 scroll-mt-36"
              >
                <h2 className="font-display text-2xl font-black text-[#1A1A1A] mb-5">{cat}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {r.menu.filter((m) => m.category === cat).map(renderItem)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Floating cart — sits clearly above the bottom nav (and its safe-area) */}
      {itemCount > 0 && (
        <div
          className="fixed inset-x-0 flex justify-center z-[60] px-6"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 5.25rem)" }}
        >
          <Button
            onClick={() => navigate("/cart")}
            className="rounded-full bg-[#1A1A1A] hover:bg-[#1A1A1A]/90 text-white px-8 py-6 text-base shadow-2xl"
            data-testid="view-cart-fab"
          >
            {lang === "ar" ? "عرض السلة" : "View cart"} · {itemCount} {lang === "ar" ? "عنصر" : "items"}
          </Button>
        </div>
      )}
    </div>
  );
}
